/**
 * Umsatzsteuer.
 *
 * Das Unternehmen handelt ausschliesslich mit Handelsware zum Regelsatz.
 * Der Steuersatz haengt trotzdem an jeder einzelnen Position und nicht
 * global am Beleg, weil sonst spaeter weder eine steuerfreie
 * innergemeinschaftliche Lieferung noch eine Ausfuhrlieferung abbildbar
 * waere, ohne das Datenmodell zu brechen.
 *
 * Steuersaetze werden als **Basispunkte** gespeichert: 1900 = 19,00 %.
 */
import { roundCommercial } from './money.js';
export const TAX_RATE_STANDARD = 1900;
export const TAX_RATE_REDUCED = 700;
export const TAX_RATE_ZERO = 0;
/**
 * Verfuegbare Steuerschluessel.
 *
 * Aktiv genutzt wird im Tagesgeschaeft nur `USt19`. Die uebrigen Eintraege
 * stehen bereit, damit ein Auslandsgeschaeft keinen Eingriff in den Code
 * erfordert - sie sind bewusst nicht mit weiterer Automatik hinterlegt.
 */
export const TAX_CODES = {
    USt19: {
        code: 'USt19',
        label: 'Umsatzsteuer 19 %',
        rate: TAX_RATE_STANDARD,
        category: 'S',
    },
    USt7: {
        code: 'USt7',
        label: 'Umsatzsteuer 7 %',
        rate: TAX_RATE_REDUCED,
        category: 'S',
    },
    IGL: {
        code: 'IGL',
        label: 'Innergemeinschaftliche Lieferung',
        rate: TAX_RATE_ZERO,
        category: 'K',
        exemptionReason: 'Steuerfreie innergemeinschaftliche Lieferung gem. § 4 Nr. 1b i.V.m. § 6a UStG',
    },
    AUSFUHR: {
        code: 'AUSFUHR',
        label: 'Ausfuhrlieferung Drittland',
        rate: TAX_RATE_ZERO,
        category: 'G',
        exemptionReason: 'Steuerfreie Ausfuhrlieferung gem. § 4 Nr. 1a i.V.m. § 6 UStG',
    },
};
export const DEFAULT_TAX_CODE = 'USt19';
export function getTaxCode(code) {
    const taxCode = TAX_CODES[code];
    if (!taxCode)
        throw new Error(`Unbekannter Steuerschluessel: ${code}`);
    return taxCode;
}
/** Steuerbetrag in Cent zu einem Nettobetrag in Cent. */
export function taxAmount(netAmount, rate) {
    return roundCommercial((netAmount * rate) / 10_000);
}
/** Nettobetrag in Cent aus einem Bruttobetrag herausrechnen. */
export function netFromGross(grossAmount, rate) {
    return roundCommercial((grossAmount * 10_000) / (10_000 + rate));
}
/**
 * Rechnet die Belegsummen aus den Positionen.
 *
 * Die Steuer wird pro Steuerschluessel auf die Summe der zugehoerigen
 * Positionen gerechnet, nicht je Position. Das ist die Vorgabe aus
 * Abschnitt 14.c UStAE und liefert bei vielen Kleinpositionen einen
 * anderen (richtigen) Betrag als eine Summe positionsweise gerundeter
 * Steuerbetraege.
 */
export function calculateTotals(lines) {
    const groups = new Map();
    for (const line of lines) {
        groups.set(line.taxCode, (groups.get(line.taxCode) ?? 0) + line.netAmount);
    }
    const taxSummary = [];
    for (const [code, netAmount] of groups) {
        const taxCode = getTaxCode(code);
        taxSummary.push({
            taxCode: code,
            label: taxCode.label,
            rate: taxCode.rate,
            category: taxCode.category,
            ...(taxCode.exemptionReason ? { exemptionReason: taxCode.exemptionReason } : {}),
            netAmount,
            taxAmount: taxAmount(netAmount, taxCode.rate),
        });
    }
    taxSummary.sort((a, b) => b.rate - a.rate);
    const netTotal = taxSummary.reduce((sum, entry) => sum + entry.netAmount, 0);
    const taxTotal = taxSummary.reduce((sum, entry) => sum + entry.taxAmount, 0);
    return { netTotal, taxTotal, grossTotal: netTotal + taxTotal, taxSummary };
}
//# sourceMappingURL=tax.js.map